import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { ModulesStore } from '../../../modules.store';
import { FormService } from '../../../features/form-builder/form.service';
import type { Entry, Module } from '../../../modules.model';
import { DatePipe } from '@angular/common';
import { ViewSwitcher } from './agenda-view/ui/view-switcher.component';
import { UserPreferenceStore } from '../../../../../../core/services/user-preference/user-preference.store';
import { UtilityService } from '../../../../../../shared/services/utility.service';
import { DateHelperService } from '../../../../../../shared/services/date-helper.service';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, merge, tap } from 'rxjs';
import { FiltersService } from '../../../features/filters/filters.service';
import { MatDialog } from '@angular/material/dialog';
import { Row } from '../planning/base-planning.model';
import { ActivatedRoute } from '@angular/router';
import { cloneDeep, isNaN } from 'lodash-es';
import { TimeRegistrationType } from './base-time-registeration.model';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseTimeRegisterationService {
  modulesStore = inject(ModulesStore);
  route = inject(ActivatedRoute);

  abstract timeRegistrationType: TimeRegistrationType;

  timeRegistrationModule = signal<Module>(this.modulesStore.getModuleByCode('time-registration'));
  view = signal<ViewSwitcher>({
    mode: 'agenda',
    agenda: 'week',
    list: 'date',
    tableSubjectKeyPath: 'user',
    tableContentModuleCode: this.timeRegistrationModule().code,
  });

  statusTaxonomy = this.modulesStore.taxonomies()[this.timeRegistrationModule().id].find(taxonomy => {
    return taxonomy.name === 'Status';
  });

  utilityService = inject(UtilityService);
  userPreferenceStore = inject(UserPreferenceStore);
  dialog = inject(MatDialog);
  formService = inject(FormService);
  filtersService = inject(FiltersService);
  datePipe = new DatePipe('nl-NL');

  selectedTimeRegisteration = computed(() => {
    return (cloneDeep(this.modulesStore.entries()[this.timeRegistrationModule().id]) || []).map(entry => ({
      ...entry,
      uiState: {
        ...entry?.uiState,
        ...this.createUiStateParameters(entry),
      },
    })) as Entry[];
  });

  totalHoursSubmitted = computed(() => {
    return this.selectedTimeRegisteration().reduce((acc, entry) => {
      if (entry.entry?.status?.slug === 'ingediend') {
        return acc + Number(entry.entry['total-hours']);
      }
      return acc;
    }, 0);
  });

  totalHoursApproved = computed(() => {
    return this.selectedTimeRegisteration().reduce((acc, entry) => {
      if (entry.entry?.status?.slug === 'goedgekeurd') {
        return acc + Number(entry.entry['total-hours']);
      }
      return acc;
    }, 0);
  });

  fromDate = computed(() => {
    return this.datePipe.transform(this.daysOfWeek()[0], 'yyyy-MM-dd');
  });
  endDate = computed(() => {
    return this.datePipe.transform(this.daysOfWeek()[6], 'yyyy-MM-dd');
  });
  week = computed(() => {
    return this.datePipe.transform(this.selectedDate(), 'w');
  });

  daysOfWeek = computed(() => {
    const days = [];
    const weekNum = this.weekNumber();
    const monday = DateHelperService.getMondayFromWeekNumber(weekNum, this.selectedDate().getFullYear());
    for (let i = 0; i < 7; i++) {
      days[i] = DateHelperService.addDays(monday, i);
    }
    return days;
  });
  selectedDate = signal(new Date());
  dayIndex = computed(() => {
    return this.datePipe.transform(this.selectedDate(), 'c');
  });
  weekNumber = computed(() => {
    return this.datePipe.transform(this.selectedDate(), 'w');
  });
  year = computed(() => {
    return this.datePipe.transform(this.selectedDate(), 'yyyy');
  });
  statusField = new FormControl<string>('');
  status = toSignal(this.statusField.valueChanges);

  days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  rows: Signal<Row[]> = computed(() => {
    const rows: Row[] = [];
    return rows;
  });

  constructor() {
    // load selected week from url

    this.setDate(
      this.route.snapshot.queryParams?.['entry.date.endDate']
        ? new Date(this.route.snapshot.queryParams?.['entry.date.endDate'])
        : new Date()
    );

    merge(toObservable(this.selectedDate))
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        tap(() => {
          if (this.timeRegistrationType !== undefined) {
            this.setDateRange();
          }

          requestAnimationFrame(() => {
            return this.loadTimeRegisterationEntries();
          });
        })
      )
      .subscribe();
  }

  createUiStateParameters(entry: Entry) {
    return {
      // totalHours: this.calculateHours(entry.entry),
      // totalHoursPerDay: this.calculateHoursPerDay(entry.entry),
    };
  }

  /**
   * Pauze rows use a 0% hour-rate (rateProcentage === 0) and must NOT count toward
   * worked-hour totals (total-hours / hours-per-day). They still appear in
   * hours-per-hourrate-type as a separate breakdown line.
   */
  private isPauseRow(hoursRow: unknown): boolean {
    const rate = (hoursRow as { 'hour-rate'?: { entry?: { rateProcentage?: unknown } } } | null | undefined)?.[
      'hour-rate'
    ]?.entry?.rateProcentage;
    return rate !== undefined && rate !== null && Number(rate) === 0;
  }

  calculateHours(entry: Entry['entry']) {
    let totalHours = 0;
    entry.hours?.forEach(hoursRow => {
      if (this.isPauseRow(hoursRow)) {
        return;
      }
      this.days.forEach(day => {
        const timeValue = Number(hoursRow?.['hours-of-week']?.[day]) ?? 0;
        if (isNaN(timeValue)) {
          return;
        }
        totalHours += timeValue;
      });
    });
    return Number(totalHours.toFixed(2));
  }

  calculateHoursPerDay(entry: Entry['entry']) {
    const totalHoursPerDay = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
      summary: '',
    };
    entry.hours?.forEach(hoursRow => {
      if (this.isPauseRow(hoursRow)) {
        return;
      }
      this.days.forEach(day => {
        const timeValue = Number(hoursRow?.['hours-of-week']?.[day]) ?? 0;
        if (isNaN(timeValue)) {
          return;
        }
        totalHoursPerDay[day] += +timeValue.toFixed(2);
      });
    });
    totalHoursPerDay.summary = Object.keys(totalHoursPerDay)
      .filter(day => day !== 'summary')
      .map((day, index) => {
        return DateHelperService.DAYS_OF_WEEK_SHORT[index] + ': ' + totalHoursPerDay[day];
      })
      .join(' | ');

    return totalHoursPerDay;
  }

  calculateHoursPerHourRateType(entry: Entry['entry']): string {
    const totalHoursPerHourRateType = {};
    entry.hours?.forEach(hoursRow => {
      const hourRateEntry = hoursRow['hour-rate'];
      if (hourRateEntry?.selectedInput == null) {
        return;
      }

      this.days.forEach(day => {
        const timeValue = Number(hoursRow?.['hours-of-week']?.[day]) ?? 0;
        if (isNaN(timeValue)) {
          return;
        }
        if (totalHoursPerHourRateType[hourRateEntry.selectedInput] === undefined) {
          totalHoursPerHourRateType[hourRateEntry.selectedInput] = 0;
        }
        totalHoursPerHourRateType[hourRateEntry.selectedInput] += +timeValue.toFixed(2);
      });
    });
    return Object.keys(totalHoursPerHourRateType)
      .map(key => {
        return key + ': ' + totalHoursPerHourRateType[key];
      })
      .join(' | ');
  }

  loadTimeRegisterationEntries() {
    const modulesId = [this.timeRegistrationModule().id];

    modulesId.forEach(moduleId => {
      this.modulesStore.loadEntries({
        moduleId: moduleId,
        offset: 0,
        orderBy: { active: 'entry.position', direction: 'asc' },
        limit: 5000,
        applyQueryParams: true,
      });
    });
  }

  updateView(view: ViewSwitcher) {
    this.view.set(view);
    this.userPreferenceStore.editModule({
      moduleId: this.timeRegistrationModule().id,
      data: { view: this.view() },
    });
  }

  addDaysToCurrentDate(days: number) {
    this.setDate(DateHelperService.addDays(this.selectedDate(), days));
  }

  setDate(date: Date) {
    if (this.timeRegistrationType === undefined) {
      return;
    }
    date = new Date(date);
    this.clearPlanningItems();
    this.selectedDate.update(() => date);
    this.setDateRange();
  }

  setDateRange() {
    const startDate = this.datePipe.transform(this.daysOfWeek()[0], 'yyyy-MM-dd') + 'T00:00:00';
    const endDate = this.datePipe.transform(this.daysOfWeek()[6], 'yyyy-MM-dd') + 'T23:59:59';

    this.filtersService.setQueryParams({
      'entry.date.startDate': startDate,
      'entry.date.endDate': endDate,
    });
  }

  clearPlanningItems() {
    const moduleIds = [this.timeRegistrationModule().id];
    this.modulesStore.clearEntries(moduleIds);
  }

  setStatus(entry: Entry, status: string) {
    this.statusTaxonomy.entries.find(taxonomyEntry => {
      if (taxonomyEntry.name === status) {
        entry.entry.status = taxonomyEntry;
      }
    });
    return entry;
  }

  sendAllTimeRegistrationOfWeek() {
    this.selectedTimeRegisteration().forEach(entry => {
      if (entry.entry.status.slug !== 'ingediend' && entry.entry.status.slug !== 'goedgekeurd') {
        entry = this.setStatus(entry, 'Indienen');
        this.modulesStore.editEntry({
          moduleId: this.timeRegistrationModule().id,
          entryId: entry.id,
          entry: {
            ...entry,
            entry: {
              ...(entry.entry as Entry['entry']),
            },
          },
        });
      }
    });
  }

  setTotals(entry: Entry['entry']): Entry['entry'] {
    if (
      entry['total-hours'] === undefined ||
      entry['total-hours'] != this.calculateHours(entry) ||
      entry['hours-per-day'] != this.calculateHoursPerDay(entry)
    ) {
      entry['total-hours'] = this.calculateHours(entry);
      entry['hours-per-day'] = this.calculateHoursPerDay(entry);
      entry['hours-per-hourrate-type'] = this.calculateHoursPerHourRateType(entry);
    }
    return entry;
  }
}
